import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, FileImage, Loader2, CheckCircle, XCircle, Edit, Trash2, Plus, Save } from 'lucide-react'
import { api } from '@/lib/api'
import Logo from '@/components/Logo'

interface ScannedItem {
  name: string
  description: string | null
  price: number
  allergens: string[]
  tags: string[]
}

interface ScannedCategory {
  name: string
  displayOrder: number
  items: ScannedItem[]
}

interface ScannedMenu {
  categories: ScannedCategory[]
  totalItems: number
  confidence: number
  languageDetected: string
}

export function MenuImportPage() {
  const navigate = useNavigate()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [importing, setImporting] = useState(false)
  const [scannedMenu, setScannedMenu] = useState<ScannedMenu | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError('Seleziona un\'immagine valida (JPG, PNG, etc.)')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Immagine troppo grande. Max 10MB')
      return
    }

    setSelectedFile(file)
    setError(null)
    setSuccess(false)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Handle drag & drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      const fakeEvent = {
        target: { files: [file] }
      } as any
      handleFileSelect(fakeEvent)
    }
  }

  // Scan menu with AI
  const handleScanMenu = async () => {
    if (!selectedFile) return

    setScanning(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('image', selectedFile)

      const response = await api.post('/api/menu/scan', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      setScannedMenu(response.data)
      console.log('Menu scanned successfully:', response.data)

    } catch (err: any) {
      console.error('Scan failed:', err)
      setError(err.response?.data?.message || 'Errore durante la scansione del menu. Riprova.')
    } finally {
      setScanning(false)
    }
  }

  // Import menu to database
  const handleImportMenu = async () => {
    if (!scannedMenu) return

    setImporting(true)
    setError(null)

    try {
      const response = await api.post('/api/menu/import', scannedMenu)

      console.log('Menu imported successfully:', response.data)
      setSuccess(true)

      // Redirect to menu page after 2 seconds
      setTimeout(() => {
        navigate('/dashboard/menu')
      }, 2000)

    } catch (err: any) {
      console.error('Import failed:', err)
      setError(err.response?.data?.message || 'Errore durante l\'importazione. Riprova.')
    } finally {
      setImporting(false)
    }
  }

  // Edit item inline
  const handleEditItem = (categoryIndex: number, itemIndex: number, field: string, value: any) => {
    if (!scannedMenu) return

    const updated = { ...scannedMenu }
    updated.categories[categoryIndex].items[itemIndex] = {
      ...updated.categories[categoryIndex].items[itemIndex],
      [field]: value
    }
    setScannedMenu(updated)
  }

  // Delete item
  const handleDeleteItem = (categoryIndex: number, itemIndex: number) => {
    if (!scannedMenu) return

    const updated = { ...scannedMenu }
    updated.categories[categoryIndex].items.splice(itemIndex, 1)
    updated.totalItems--
    setScannedMenu(updated)
  }

  // Delete category
  const handleDeleteCategory = (categoryIndex: number) => {
    if (!scannedMenu) return

    const updated = { ...scannedMenu }
    const itemsCount = updated.categories[categoryIndex].items.length
    updated.categories.splice(categoryIndex, 1)
    updated.totalItems -= itemsCount
    setScannedMenu(updated)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Logo size={40} variant="icon" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Importa Menu con AI</h1>
                <p className="text-sm text-gray-600">Digitalizza il tuo menu in 2 minuti</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard/menu')}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Torna al Menu
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <h3 className="font-semibold text-green-900">Menu importato con successo! 🎉</h3>
                <p className="text-sm text-green-700">Reindirizzamento alla pagina menu...</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <XCircle className="text-red-600" size={24} />
              <div>
                <h3 className="font-semibold text-red-900">Errore</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">

          {/* Left: Upload Section */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">1. Carica Foto Menu</h2>

              {/* Upload Area */}
              {!previewUrl ? (
                <div
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-400 transition cursor-pointer"
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <FileImage className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600 font-medium mb-2">
                      Trascina un'immagine o clicca per caricare
                    </p>
                    <p className="text-sm text-gray-500">
                      JPG, PNG, max 10MB
                    </p>
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Menu preview"
                      className="w-full rounded-lg border"
                    />
                    <button
                      onClick={() => {
                        setPreviewUrl(null)
                        setSelectedFile(null)
                        setScannedMenu(null)
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {!scannedMenu && (
                    <button
                      onClick={handleScanMenu}
                      disabled={scanning}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {scanning ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Scansione in corso...
                        </>
                      ) : (
                        <>
                          <Upload size={20} />
                          Scansiona Menu con AI
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">💡 Come funziona</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Scatta foto del menu cartaceo</li>
                  <li>✓ L'AI estrae piatti, prezzi, allergeni</li>
                  <li>✓ Verifica e correggi se necessario</li>
                  <li>✓ Importa nel database</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Right: Scanned Results */}
          <div>
            {scannedMenu ? (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold">2. Risultato Scansione</h2>
                    <p className="text-sm text-gray-600">
                      {scannedMenu.totalItems} piatti trovati •
                      Confidence: {Math.round(scannedMenu.confidence * 100)}%
                    </p>
                  </div>
                  <button
                    onClick={handleImportMenu}
                    disabled={importing}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Importazione...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Importa Menu
                      </>
                    )}
                  </button>
                </div>

                {/* Categories & Items */}
                <div className="space-y-6 max-h-[600px] overflow-y-auto">
                  {scannedMenu.categories.map((category, catIndex) => (
                    <div key={catIndex} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-lg text-gray-900">{category.name}</h3>
                        <button
                          onClick={() => handleDeleteCategory(catIndex)}
                          className="text-red-600 hover:text-red-800"
                          title="Elimina categoria"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="space-y-3">
                        {category.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="bg-gray-50 rounded p-3 relative group">
                            <button
                              onClick={() => handleDeleteItem(catIndex, itemIndex)}
                              className="absolute top-2 right-2 text-red-600 hover:text-red-800 opacity-0 group-hover:opacity-100 transition"
                              title="Elimina piatto"
                            >
                              <Trash2 size={14} />
                            </button>

                            <div className="flex justify-between items-start mb-2">
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => handleEditItem(catIndex, itemIndex, 'name', e.target.value)}
                                className="font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 outline-none flex-1 mr-2"
                              />
                              <input
                                type="number"
                                step="0.01"
                                value={item.price}
                                onChange={(e) => handleEditItem(catIndex, itemIndex, 'price', parseFloat(e.target.value))}
                                className="font-semibold text-blue-600 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 outline-none w-20 text-right"
                              />
                              <span className="ml-1 text-blue-600">€</span>
                            </div>

                            {item.description && (
                              <textarea
                                value={item.description}
                                onChange={(e) => handleEditItem(catIndex, itemIndex, 'description', e.target.value)}
                                className="text-sm text-gray-600 w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 outline-none resize-none"
                                rows={2}
                              />
                            )}

                            {item.allergens && item.allergens.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.allergens.map((allergen, i) => (
                                  <span key={i} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                    {allergen}
                                  </span>
                                ))}
                              </div>
                            )}

                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.tags.map((tag, i) => (
                                  <span key={i} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                <FileImage className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500">
                  Carica un'immagine del menu per iniziare
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default MenuImportPage
